import { createContext, useState, useEffect, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { apiLog } from '../utils/debugConfig.js';
import { useVersionedApi } from '../utils/versionedApi';
import { isRestrictedAccount } from '../utils/accountKind';

// Shared state for sidebar permissions + program list. Used to be a plain
// hook that every consumer re-fetched independently (9+ call sites). On a
// cold Azure container that produced ~12 redundant /admin/my-sidebar and
// /programs requests on first admin render and could leave different
// components with inconsistent state when one of the requests failed.
// A single Provider fetches once and broadcasts.
export const IntegratedSidebarContext = createContext(null);

export const IntegratedSidebarProvider = ({ children }) => {
  const [sidebarItems, setSidebarItems] = useState([]);
  const [permissions, setPermissions] = useState({});
  // Server's program-access decision per program key (null = not provided).
  const [programAccess, setProgramAccess] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Whether `permissions` is the server's answer: 'loaded' (a permission set,
  // possibly empty), 'denied' (403/404: no permission set for this user),
  // 'failed' (no answer: 5xx or network) or 'loading'. An empty set means "no
  // access" only when answered; route guards word their message on this.
  const [permissionsStatus, setPermissionsStatus] = useState('loading');
  const { user, authChecked } = useAuth();
  const apiClient = useVersionedApi('v1');

  // Have we ever successfully loaded a permission set in this document? Used to
  // decide whether a FAILED fetch should surface the error screen or be
  // swallowed in favour of the last-known-good state (see below).
  const hasLoadedOnceRef = useRef(false);

  useEffect(() => {
    // A cached user is not a verified session. Until the startup check has
    // finished, both requests below would go out on a possibly-expired cookie and
    // come back 400 (no tenant context), which the retry loop then hammered.
    if (authChecked === false) return;
    // Don't fire until we know who the user is (logged in vs anonymous).
    // Both endpoints below are org-scoped, so platform admins (no organizationId)
    // would only get 401s — the platform shell renders its own nav.
    // A restricted login (pharmacy) has no sidebar; the API refuses these requests.
    if (isRestrictedAccount(user)) {
      setLoading(false);
      return;
    }
    if (!user || !user.organizationId) {
      // A logged-in user with no organizationId is only legitimate for platform
      // admins (organizationId === null), who render their own shell. Anything
      // else means the /users/me payload lost the field, and this early return
      // silently skips the fetch — permissions stay {} and every guarded route
      // renders "Could not load your permissions" with no request in the network
      // tab and nothing in the console. That exact omission cost days of
      // misdiagnosis, so make it loud.
      if (user && user.organizationId === undefined) {
        console.error(
          '[IntegratedSidebar] user.organizationId is missing from the /users/me payload — ' +
          'skipping sidebar fetch. Permissions will be empty. This is a backend contract bug, ' +
          'not a permissions problem.'
        );
      }
      setLoading(false);
      return;
    }

    let cancelled = false;

    // The /admin/my-sidebar build is heavy (permission resolution + bundle
    // lookup, ~1.5s of DB work even warm) and can fail transiently: a cold Azure
    // replica's first request, brief DB/pool pressure, a Redis blip, a
    // deploy-cutover window, or a retryable 503 from tenantMiddleware. A single
    // such failure used to dead-end the user on "Could not load your permissions"
    // with only a manual reload — which often hit the same cold window again.
    // This recurred across users (Donnyel, Kathryn, Gilbert, Gifty).
    //
    // Retry the sidebar fetch a few times with exponential backoff before giving
    // up. The endpoint is server-cached (short TTL), so a retry after the first
    // slow/cold call almost always succeeds. Genuine auth rejections (401/403)
    // are NOT retried — retrying wouldn't help and would just spin.
    const MAX_SIDEBAR_ATTEMPTS = 4;
    const getSidebarWithRetry = async () => {
      let lastErr;
      for (let attempt = 1; attempt <= MAX_SIDEBAR_ATTEMPTS; attempt++) {
        try {
          return await apiClient.get('/admin/my-sidebar');
        } catch (err) {
          lastErr = err;
          const status = err?.response?.status;
          // Any 4xx is an answer (auth, tenant context, validation), not a blip:
          // retrying it cannot help and only multiplies the noise.
          if (status >= 400 && status < 500) throw err;
          if (attempt < MAX_SIDEBAR_ATTEMPTS && !cancelled) {
            // 0.5s, 1s, 2s
            await new Promise((r) => setTimeout(r, 500 * Math.pow(2, attempt - 1)));
          }
        }
      }
      throw lastErr;
    };

    const fetchSidebar = async () => {
      try {
        setLoading(true);
        apiLog('IntegratedSidebar: fetching sidebar + programs');

        const [sidebarResponse, programsResponse] = await Promise.allSettled([
          getSidebarWithRetry(),
          apiClient.get('/programs'),
        ]);

        if (cancelled) return;

        // A failed fetch must NEVER destroy a permission set we already have.
        // This effect re-runs whenever the `user` object identity changes (an
        // auth refresh, a storage event from a second window, a /users/me
        // re-fetch), so it is a REFETCH far more often than a first load. The
        // old code wiped permissions to {} on any failure, which flipped a
        // working session onto the "Could not load your permissions" screen
        // mid-task — reported by Gifty while completing an FA after opening a
        // second window to read a DA, and reproducible in incognito (so it was
        // never the stale-bundle issue).
        //
        // Keeping the last-known-good client state is a UX decision, not a
        // security one: real access is enforced server-side on every request by
        // authMiddleware + requireBundle. The client map only decides which nav
        // items and routes to render.
        if (sidebarResponse.status === 'fulfilled') {
          const response = sidebarResponse.value;
          apiLog('IntegratedSidebar loaded:', {
            items: response.data?.sidebarItems?.length || 0,
            perms: Object.keys(response.data?.permissions || {}).length,
          });
          setSidebarItems(response.data?.sidebarItems || []);
          setPermissions(response.data?.permissions || {});
          setProgramAccess(response.data?.programAccess || null);
          setPermissionsStatus('loaded');
          hasLoadedOnceRef.current = true;
        } else {
          console.error('IntegratedSidebar: /admin/my-sidebar failed:', sidebarResponse.reason?.message);
          // Only clear when there is nothing good to preserve (true first load).
          if (!hasLoadedOnceRef.current) {
            setSidebarItems([]);
            setPermissions({});
            const status = sidebarResponse.reason?.response?.status;
            setPermissionsStatus(status === 403 || status === 404 ? 'denied' : 'failed');
          }
        }

        if (programsResponse.status === 'fulfilled') {
          const programsData = programsResponse.value?.data || [];
          apiLog('IntegratedSidebar: programs loaded:', programsData.length);
          setPrograms(programsData);
        } else {
          console.error('IntegratedSidebar: /programs failed:', programsResponse.reason?.message);
          if (!hasLoadedOnceRef.current) setPrograms([]);
        }

        // Never leave a stale error latched once we hold a usable permission set.
        setError(null);
      } catch (err) {
        if (cancelled) return;
        console.error('IntegratedSidebar: unexpected failure:', err?.message);
        // Same rule on the unexpected path: a previously-good session keeps
        // working instead of being bounced to the error screen.
        if (!hasLoadedOnceRef.current) {
          setError(err);
          setSidebarItems([]);
          setPermissions({});
          setPrograms([]);
          setPermissionsStatus('failed');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSidebar();
    return () => { cancelled = true; };
  }, [user, authChecked, apiClient]);

  return (
    <IntegratedSidebarContext.Provider
      value={{ sidebarItems, permissions, programAccess, programs, loading, error, permissionsStatus }}
    >
      {children}
    </IntegratedSidebarContext.Provider>
  );
};
